'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import credits_item_bg from '@/app/assets/imgs/purchase_credits/credits_item_bg.jpeg';
import { Credits } from '@/app/components/icons/Credits';
import { CouponBadge } from '@/app/components/shared/CouponBadge';
import type { CouponType, CreditsPurchaseProduct } from '@/app/types';
import { cn, formatPrice, formatPriceDiscount, getPriceDiscount, hasSelectedCoupon } from '@/app/utils';
import { useLocale } from '@/i18n/routing';

const CREDITS_PACK_COPY_KEY: Partial<Record<number, string>> = {
  3000: 'pack3000',
  20000: 'pack20000',
};

const CreditsItem: FC<{
  active?: boolean;
  product: CreditsPurchaseProduct;
  setCurrentProduct: (product: CreditsPurchaseProduct) => void;
}> = ({ active = false, product, setCurrentProduct }) => {
  const locale = useLocale();
  const t = useTranslations();
  const { available_coupons = [], original_price, price } = product;
  const discount = getPriceDiscount(price, original_price ?? 0);

  const isUltimateV2OffPeakCoupon = available_coupons.some(
    (coupon: CouponType) => coupon.name === 'ultimate_v2_off_peak_coupon' && coupon.is_best === true,
  );
  const hasCoupon = hasSelectedCoupon(product);
  const packCopyKey = CREDITS_PACK_COPY_KEY[product.credits_gift];
  const packCopy = packCopyKey ? t(`popup.dialog.purchaseCredits.packCopy.${packCopyKey}`) : undefined;
  const cardContent = (
    <div className="flex h-full w-full flex-col items-center rounded-10 bg-system-bg03 p-[5px] sm:py-[9px]">
      <div className="mb-1 h-5 place-self-end sm:mb-2 sm:place-self-center">
        {discount > 0 &&
          (hasCoupon ? (
            <CouponBadge discount={discount} />
          ) : (
            <div
              className={cn(
                'inline-block rounded-4 bg-[#FF7716] px-2 text-[12px]',
                isUltimateV2OffPeakCoupon && 'bg-ShengshuButton text-black',
              )}
            >
              {t('popup.dialog.purchaseCredits.discountText', {
                discount: formatPriceDiscount(discount, locale),
              })}
            </div>
          ))}
      </div>
      <div className="mb-1 flex items-center">
        <Credits variant="purchaseCredits" className="mr-[2px] h-5 w-5 sm:h-4 sm:w-4" />
        <span className="text-2xl text-[#94D8FF] leading-[1.4] sm:text-xl">{product.credits_gift}</span>
      </div>
      <div className="text-base leading-[1.4]">{formatPrice(price)}</div>
    </div>
  );

  return (
    <div
      className={cn(
        'cursor-pointer',
        packCopy
          ? 'flex h-[148px] flex-col items-center overflow-hidden rounded-10 bg-[#5765F2]'
          : 'h-[120px] rounded-10 bg-center bg-cover p-[2px]',
      )}
      style={
        packCopy
          ? undefined
          : {
              ...(active &&
                !hasCoupon && {
                  backgroundImage: `url(${credits_item_bg.src})`,
                }),
              ...(active &&
                hasCoupon && {
                  background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                }),
            }
      }
      onClick={() => {
        setCurrentProduct(product);
      }}
    >
      {packCopy && (
        <div className="flex h-7 shrink-0 items-center justify-center px-1 font-medium text-sm text-white leading-5">
          {packCopy}
        </div>
      )}
      {packCopy ? (
        <div
          className="h-[120px] w-full rounded-10 bg-center bg-cover p-[2px]"
          style={{
            background: active
              ? hasCoupon
                ? 'linear-gradient(135deg, #6366f1, #a855f7)'
                : `url(${credits_item_bg.src}) center / cover`
              : '#5765F2',
          }}
        >
          {cardContent}
        </div>
      ) : (
        cardContent
      )}
    </div>
  );
};

export default CreditsItem;
